import { useEffect, useRef, useState } from "react";

import { fetchOneEntry } from "@builder.io/sdk-react";

import { ModelMap } from "~/hec/builder/model-map";
import { useBuilder } from "~/hec/hooks/useBuilder";

/**
 * SFCC-11727 (ARTICLES-13, fast-follow to SFCC-11364) — resolve the parent-goal
 * TITLE for the `Goal: <title>` crumb on a module `learning-center-page`.
 *
 * A module-page `learning-center-page` entry inlines only `data.module`, NOT
 * `data.goal`, so the parent-goal title is unresolvable from `BuilderContext`
 * alone (the goal `href` already resolves from the module id's goal slug — only
 * the TITLE is missing). The composable resolves it with a two-step reverse
 * client fetch (`useFetchParentLevel` → `useFetchParentGoal`): given the module
 * entry's content id, find the level whose `data.modules[].module.id` matches,
 * then the goal whose `data.levels[].level.id` matches that level, and read its
 * `data.title`.
 *
 * Mirrors that chain via the SAME client Builder-fetch convention shipped for
 * the GoalTile dashboard ref (SFCC-11732): `fetchOneEntry` keyed by an
 * `$elemMatch`-style reverse-lookup query, `enrich: false` (we only need the
 * goal title — no nested refs), `userAttributes`, and
 * `AbortSignal.timeout(2500)`. On any miss/timeout the hook returns `undefined`
 * and the breadcrumb falls back to the current module-only trail (`Home › HSA
 * Learning Center › Level N: Module M`) — a verifiable, non-broken subset.
 *
 * The lookup is keyed on the module content id (the targeting dimension), so a
 * navigation to a different module re-runs the chain; the in-flight resolution
 * is guarded per module id so a re-render can't fire a duplicate request and a
 * stale result can't overwrite a newer one.
 */

const FETCH_TIMEOUT_MS = 2500;

/** Resolve the parent level's content id from the module content id. */
async function fetchParentLevelId(
  moduleContentId: string,
  apiKey: string,
): Promise<string | undefined> {
  const entry = await fetchOneEntry({
    model: ModelMap.learningCenterLevel,
    apiKey,
    userAttributes: { useScheduling: true },
    options: {
      enrich: false,
      query: {
        "data.modules.module.id": moduleContentId,
      },
      limit: 1,
      fields: "id",
    },
    fetchOptions: { signal: AbortSignal.timeout(FETCH_TIMEOUT_MS) },
  });
  return entry?.id;
}

/** Resolve the parent goal's title from the parent level's content id. */
async function fetchParentGoalTitle(
  levelContentId: string,
  apiKey: string,
): Promise<string | undefined> {
  const entry = await fetchOneEntry({
    model: ModelMap.learningCenterGoal,
    apiKey,
    userAttributes: { useScheduling: true },
    options: {
      enrich: false,
      query: {
        "data.levels.level.id": levelContentId,
      },
      limit: 1,
      fields: "data.title",
    },
    fetchOptions: { signal: AbortSignal.timeout(FETCH_TIMEOUT_MS) },
  });
  const title = (entry?.data as { title?: unknown } | undefined)?.title;
  return typeof title === "string" && title.length > 0 ? title : undefined;
}

export interface UseParentGoalTitleResult {
  title: string | undefined;
  isLoading: boolean;
}

/**
 * Resolve the parent-goal title for a module page via the reverse chain
 * module → parent level → parent goal. No-ops (returns `undefined`) when:
 *  - `enabled` is false (e.g. the goal title is already inlined, so no fetch),
 *  - there is no module content id, or
 *  - the Builder api key is unavailable.
 *
 * @param moduleContentId The module entry's Builder content id (`data.module.id`
 *   on the page entry — the reference id, NOT the parsed `data.module` slug).
 * @param enabled Gate the fetch; pass `false` when a title is already resolvable
 *   from inlined data so the hook stays inert.
 */
export function useParentGoalTitle(
  moduleContentId: string | undefined,
  enabled: boolean,
): UseParentGoalTitleResult {
  const { config } = useBuilder();
  const apiKey = config.api_key;

  const [title, setTitle] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  // Guard the in-flight chain per module id so a re-render can't fire a
  // duplicate request, and a stale result can't overwrite a newer one.
  const inFlightFor = useRef<string | null>(null);

  const shouldFetch = enabled && !!moduleContentId && !!apiKey;

  useEffect(() => {
    if (!shouldFetch || !moduleContentId || !apiKey) {
      // Clear any stale title from a previous module when the gate closes.
      setTitle(undefined);
      setIsLoading(false);
      inFlightFor.current = null;
      return;
    }

    let cancelled = false;
    inFlightFor.current = moduleContentId;
    setIsLoading(true);
    setTitle(undefined);

    void (async () => {
      try {
        const levelId = await fetchParentLevelId(moduleContentId, apiKey);
        if (cancelled || inFlightFor.current !== moduleContentId) return;
        if (!levelId) {
          setIsLoading(false);
          return;
        }
        const goalTitle = await fetchParentGoalTitle(levelId, apiKey);
        if (cancelled || inFlightFor.current !== moduleContentId) return;
        setTitle(goalTitle);
        setIsLoading(false);
      } catch (error) {
        if (cancelled || inFlightFor.current !== moduleContentId) return;
        // Timeout / network / Builder error → fall back to the module-only
        // trail (the breadcrumb omits the goal crumb when title is undefined).
        console.error(
          `[LearningCenterBreadcrumb] Failed to resolve parent goal title for module ${moduleContentId}:`,
          error,
        );
        setTitle(undefined);
        setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [shouldFetch, moduleContentId, apiKey]);

  return { title, isLoading };
}

export default useParentGoalTitle;
